import { configFile } from "../configFile";
import {
  addDays,
  endOfDay,
  format,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";
import hu from "date-fns/locale/hu";

interface IClosurePeriod {
  from: string;
  to: string;
  reason?: string;
}

export interface IClosureState {
  isClosed: boolean;
  reopenDate: Date | null;
  message: string;
}

const noClosure: IClosureState = {
  isClosed: false,
  reopenDate: null,
  message: "",
};

// A hibásan megadott időszakot (érvénytelen dátum, felcserélt from/to) figyelmen
// kívül hagyjuk: az isWithinInterval ilyenkor RangeError-t dobna.
function isValidPeriod(period: IClosurePeriod): boolean {
  const start = parseISO(period.from);
  const end = parseISO(period.to);

  return (
    !isNaN(start.getTime()) &&
    !isNaN(end.getTime()) &&
    start.getTime() <= end.getTime()
  );
}

function findPeriodForDate(
  periods: IClosurePeriod[],
  target: Date
): IClosurePeriod | undefined {
  return periods.find(
    (period) =>
      isValidPeriod(period) &&
      isWithinInterval(target, {
        start: startOfDay(parseISO(period.from)),
        end: endOfDay(parseISO(period.to)),
      })
  );
}

// Egymást követő zárva tartási időszakokat is átlép, hogy a nyitás napja
// mindig a tényleges első nyitva tartó nap legyen.
function getReopenDate(
  periods: IClosurePeriod[],
  activePeriod: IClosurePeriod
): Date {
  let reopenDate = startOfDay(addDays(parseISO(activePeriod.to), 1));

  // A ciklus hibás konfigurációnál (to < from) sem futhat végtelenre.
  for (let i = 0; i < 366; i += 1) {
    const nextPeriod = findPeriodForDate(periods, reopenDate);
    if (!nextPeriod) break;
    reopenDate = startOfDay(addDays(parseISO(nextPeriod.to), 1));
  }

  return reopenDate;
}

export function getClosureState(city?: string): IClosureState {
  const cityClosure = configFile.closures.find((entry) => entry.city === city);

  if (!cityClosure || cityClosure.periods.length === 0) return noClosure;

  const activePeriod = findPeriodForDate(cityClosure.periods, new Date());

  if (!activePeriod) return noClosure;

  const reopenDate = getReopenDate(cityClosure.periods, activePeriod);
  const reopenText = format(reopenDate, "MMMM d.", { locale: hu });

  const closedText = activePeriod.reason
    ? `${cityClosure.label} üzletünk zárva van (${activePeriod.reason}).`
    : `${cityClosure.label} üzletünk zárva van.`;

  return {
    isClosed: true,
    reopenDate,
    message: `${closedText} Rendelést legközelebb ${reopenText} napjától tudsz leadni.`,
  };
}
